import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Skull, Plus, Pencil, Trash2, Shield, RefreshCw, Archive, CheckCircle2, AlertTriangle, ExternalLink } from "lucide-react";
import api, { apiError } from "../lib/api";
import { Money } from "../context/PrivacyContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useAuth } from "../context/AuthContext";

const EMPTY = {
  title: "",
  zombie_type: "Otro",
  provider_id: null,
  provider_name: "",
  entity: "",
  amount: "",
  detected_at: new Date().toISOString().slice(0, 10),
  claimed_at: "",
  state: "Detectado",
  risk_new_charge: true,
  expected_frequency: "",
  notes: "",
  reference_url: "",
};

const STATE_COLORS = {
  "Detectado": "bg-amber-100 text-amber-900 border-amber-300",
  "En reclamación": "bg-orange-100 text-orange-900 border-orange-300",
  "Pendiente devolución": "bg-blue-100 text-blue-900 border-blue-300",
  "Pendiente cierre": "bg-purple-100 text-purple-900 border-purple-300",
  "Resuelto": "bg-emerald-100 text-emerald-900 border-emerald-300",
  "Archivado": "bg-neutral-200 text-neutral-700 border-neutral-300",
};

function StateBadge({ state }) {
  return (
    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${STATE_COLORS[state] || "bg-secondary"}`}>{state}</span>
  );
}

// Diálogo genérico para PIN
function PinDialog({ open, onClose, onConfirm, title, description }) {
  const [pin, setPin] = useState("");
  useEffect(() => { if (open) setPin(""); }, [open]);
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xs">
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Shield size={16} /> {title}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">{description}</p>
          <Input data-testid="zombie-pin-input" type="password" inputMode="numeric" maxLength={6} placeholder="PIN maestro" value={pin} onChange={(e) => setPin(e.target.value)} autoFocus />
          <Button data-testid="zombie-pin-confirm" className="w-full" onClick={() => onConfirm(pin)} disabled={!pin}>Confirmar</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function GastosZombie() {
  const { user } = useAuth();
  const [meta, setMeta] = useState({ types: [], states: [], active_states: [], protected_states: [] });
  const [items, setItems] = useState(null);
  const [providers, setProviders] = useState([]);
  const [filter, setFilter] = useState({ state: "activos", search: "" });
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [pinAction, setPinAction] = useState(null); // {type: 'state'|'delete', zombieId, newState}

  const loadAll = async () => {
    const params = filter.state === "todos" ? { include_archived: true } : filter.state === "activos" ? {} : { state: filter.state };
    const [ls, ss] = await Promise.all([
      api.get("/zombie", { params }),
      api.get("/zombie/summary"),
    ]);
    setItems(ls.data);
    setSummary(ss.data);
  };

  useEffect(() => {
    api.get("/zombie/meta").then((r) => setMeta(r.data));
    api.get("/providers").then((r) => setProviders(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadAll().catch((e) => toast.error(apiError(e))); }, [filter.state]);

  const filtered = useMemo(() => {
    const q = filter.search.trim().toLowerCase();
    return (items || []).filter((z) => !q || z.title.toLowerCase().includes(q) || (z.provider_name || "").toLowerCase().includes(q));
  }, [items, filter.search]);

  const openCreate = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (z) => {
    setForm({
      title: z.title,
      zombie_type: z.zombie_type,
      provider_id: z.provider_id || null,
      provider_name: z.provider_name || "",
      entity: z.entity || "",
      amount: z.amount ?? "",
      detected_at: z.detected_at || "",
      claimed_at: z.claimed_at || "",
      state: z.state,
      risk_new_charge: !!z.risk_new_charge,
      expected_frequency: z.expected_frequency || "",
      notes: z.notes || "",
      reference_url: z.reference_url || "",
    });
    setEditId(z.id);
    setOpen(true);
  };

  const submit = async () => {
    try {
      const payload = {
        ...form,
        amount: parseFloat(String(form.amount).replace(",", ".")) || 0,
        provider_id: form.provider_id || null,
        provider_name: form.provider_name || null,
        claimed_at: form.claimed_at || null,
      };
      if (editId) await api.put(`/zombie/${editId}`, payload);
      else await api.post("/zombie", payload);
      toast.success(editId ? "Actualizado" : "Incidencia creada");
      setOpen(false); setEditId(null); setForm(EMPTY);
      await loadAll();
    } catch (e) { toast.error(apiError(e)); }
  };

  const isProtected = (fromState, toState) =>
    (meta.protected_states || []).includes(toState) ||
    ((meta.protected_states || []).includes(fromState) && (meta.active_states || []).includes(toState));

  const changeState = async (z, newState) => {
    // Si requiere PIN → abrir diálogo
    if (isProtected(z.state, newState) && user?.has_pin) {
      setPinAction({ type: "state", zombieId: z.id, newState });
      return;
    }
    try {
      await api.post(`/zombie/${z.id}/state`, { state: newState });
      toast.success(`Estado → ${newState}`);
      await loadAll();
    } catch (e) { toast.error(apiError(e)); }
  };

  const confirmPin = async (pin) => {
    if (!pinAction) return;
    try {
      if (pinAction.type === "state") {
        await api.post(`/zombie/${pinAction.zombieId}/state`, { state: pinAction.newState, pin });
        toast.success(`Estado → ${pinAction.newState}`);
      } else if (pinAction.type === "delete") {
        await api.post(`/zombie/${pinAction.zombieId}/delete`, { state: "Archivado", pin });
        toast.success("Eliminado");
      }
      setPinAction(null);
      await loadAll();
    } catch (e) { toast.error(apiError(e)); }
  };

  const removeZombie = async (z) => {
    if (!window.confirm(`¿Eliminar la incidencia "${z.title}"?`)) return;
    if (user?.has_pin) { setPinAction({ type: "delete", zombieId: z.id }); return; }
    try {
      await api.delete(`/zombie/${z.id}`);
      toast.success("Eliminado");
      await loadAll();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="gastos-zombie-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tighter flex items-center gap-2">
            <Skull size={26} className="text-emerald-700" /> Gastos Zombie
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">La memoria financiera de tu familia. Registra cargos que <em>deberían</em> estar cerrados pero pueden reaparecer: suscripciones canceladas, financiaciones anuladas, cargos duplicados o fraudes.</p>
        </div>
        <Button data-testid="add-zombie-btn" onClick={openCreate}>
          <Plus size={16} className="mr-1" /> Nueva incidencia
        </Button>
      </div>

      {/* KPIs resumen */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="border border-border rounded-lg p-4">
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">Incidencias activas</div>
            <div className="text-2xl font-semibold tabular mt-1" data-testid="kpi-count">{summary.count_active}</div>
          </div>
          <div className="border border-border rounded-lg p-4">
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">Importe bajo vigilancia</div>
            <Money value={summary.total_watched} testId="kpi-total" className="block mt-1 text-2xl font-semibold tabular" />
          </div>
          <div className="border border-border rounded-lg p-4">
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">En reclamación</div>
            <div className="text-2xl font-semibold tabular mt-1">{summary.by_state?.["En reclamación"] || 0}</div>
          </div>
          <div className="border border-border rounded-lg p-4">
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">Última detección</div>
            <div className="text-sm font-medium mt-1">{summary.last_detected ? new Date(summary.last_detected).toLocaleDateString("es-ES") : "—"}</div>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="flex flex-wrap gap-2 items-center">
        <Select value={filter.state} onValueChange={(v) => setFilter({ ...filter, state: v })}>
          <SelectTrigger data-testid="filter-state" className="w-56"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="activos">Solo activas</SelectItem>
            <SelectItem value="todos">Todas (incl. archivadas)</SelectItem>
            {(meta.states || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input data-testid="filter-search" className="flex-1 min-w-[180px]" placeholder="Buscar por título o proveedor…" value={filter.search} onChange={(e) => setFilter({ ...filter, search: e.target.value })} />
      </div>

      {/* Lista */}
      <div className="border border-border rounded-lg divide-y divide-border" data-testid="zombie-list">
        {items === null ? (
          <div className="p-5 text-sm text-muted-foreground">Cargando…</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            Sin incidencias. Cuando detectes un cargo indebido, suscripción cancelada o financiación que pueda reaparecer, regístralo aquí para vigilarlo.
          </div>
        ) : filtered.map((z) => (
          <div key={z.id} className="p-4 hover:bg-secondary/40 transition-colors" data-testid={`zombie-row-${z.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-medium">{z.title}</div>
                  <StateBadge state={z.state} />
                  {z.risk_new_charge && (meta.active_states || []).includes(z.state) && (
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border border-red-300 bg-red-50 text-red-800 flex items-center gap-1">
                      <AlertTriangle size={10} /> Riesgo activo
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-3 gap-y-1">
                  <span>{z.zombie_type}</span>
                  {z.provider_name && <span>· {z.provider_name}</span>}
                  {z.entity && <span>· {z.entity}</span>}
                  <span>· Detectado {z.detected_at}</span>
                  {z.expected_frequency && <span>· {z.expected_frequency}</span>}
                </div>
                {z.notes && <div className="text-xs text-muted-foreground mt-1 italic line-clamp-2">{z.notes}</div>}
                {z.reference_url && (
                  <a href={z.reference_url} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline mt-1 inline-flex items-center gap-1">
                    <ExternalLink size={11} /> Referencia
                  </a>
                )}
              </div>
              <div className="flex items-start gap-3">
                <Money value={z.amount || 0} className="font-semibold tabular text-lg" />
              </div>
            </div>
            {/* Acciones */}
            <div className="mt-3 flex flex-wrap gap-2">
              {(meta.active_states || []).includes(z.state) && (
                <>
                  {z.state !== "En reclamación" && (
                    <Button data-testid={`zombie-claim-${z.id}`} size="sm" variant="outline" onClick={() => changeState(z, "En reclamación")}>Reclamar</Button>
                  )}
                  <Button data-testid={`zombie-resolve-${z.id}`} size="sm" variant="outline" onClick={() => changeState(z, "Resuelto")}>
                    <CheckCircle2 size={13} className="mr-1" /> Resolver
                  </Button>
                  <Button data-testid={`zombie-archive-${z.id}`} size="sm" variant="outline" onClick={() => changeState(z, "Archivado")}>
                    <Archive size={13} className="mr-1" /> Archivar
                  </Button>
                </>
              )}
              {(meta.protected_states || []).includes(z.state) && (
                <Button data-testid={`zombie-reopen-${z.id}`} size="sm" variant="outline" onClick={() => changeState(z, "Detectado")}>
                  <RefreshCw size={13} className="mr-1" /> Reincorporar a vigilancia
                </Button>
              )}
              <Button data-testid={`zombie-edit-${z.id}`} size="sm" variant="ghost" onClick={() => openEdit(z)}><Pencil size={13} /></Button>
              <Button data-testid={`zombie-delete-${z.id}`} size="sm" variant="ghost" className="text-red-600 hover:text-red-700" onClick={() => removeZombie(z)}><Trash2 size={13} /></Button>
            </div>
          </div>
        ))}
      </div>

      {/* Diálogo crear/editar */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editId ? "Editar incidencia" : "Nueva incidencia zombie"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input data-testid="zombie-title" placeholder="Título (ej. Suscripción Hostalia cancelada)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">Tipo</label>
                <Select value={form.zombie_type} onValueChange={(v) => setForm({ ...form, zombie_type: v })}>
                  <SelectTrigger data-testid="zombie-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{(meta.types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Estado</label>
                <Select value={form.state} onValueChange={(v) => setForm({ ...form, state: v })}>
                  <SelectTrigger data-testid="zombie-state"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(meta.active_states || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">Proveedor</label>
                <Select value={form.provider_id || "__none__"} onValueChange={(v) => setForm({ ...form, provider_id: v === "__none__" ? null : v, provider_name: v === "__none__" ? form.provider_name : (providers.find((p) => p.id === v)?.name || "") })}>
                  <SelectTrigger data-testid="zombie-provider"><SelectValue placeholder="Del catálogo" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Sin proveedor / texto libre —</SelectItem>
                    {providers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Input data-testid="zombie-provider-name" placeholder="Nombre proveedor (texto libre)" value={form.provider_name} onChange={(e) => setForm({ ...form, provider_name: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input data-testid="zombie-entity" placeholder="Entidad / IBAN (opcional)" value={form.entity} onChange={(e) => setForm({ ...form, entity: e.target.value })} />
              <Input data-testid="zombie-amount" placeholder="Importe afectado (€)" inputMode="decimal" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">Fecha detección</label>
                <Input data-testid="zombie-detected" type="date" value={form.detected_at} onChange={(e) => setForm({ ...form, detected_at: e.target.value })} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Fecha reclamación</label>
                <Input data-testid="zombie-claimed" type="date" value={form.claimed_at} onChange={(e) => setForm({ ...form, claimed_at: e.target.value })} />
              </div>
            </div>
            <div className="flex items-center justify-between border border-border rounded-lg px-3 py-2">
              <div>
                <div className="text-sm font-medium">Riesgo de nuevo cobro</div>
                <div className="text-xs text-muted-foreground">Se seguirá vigilando activamente</div>
              </div>
              <Switch data-testid="zombie-risk" checked={form.risk_new_charge} onCheckedChange={(v) => setForm({ ...form, risk_new_charge: v })} />
            </div>
            <Input data-testid="zombie-frequency" placeholder="Frecuencia esperada (mensual, anual, puntual…)" value={form.expected_frequency} onChange={(e) => setForm({ ...form, expected_frequency: e.target.value })} />
            <Textarea data-testid="zombie-notes" placeholder="Observaciones" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} />
            <Input data-testid="zombie-url" placeholder="URL o referencia de reclamación" value={form.reference_url} onChange={(e) => setForm({ ...form, reference_url: e.target.value })} />
            <Button data-testid="zombie-submit" className="w-full" onClick={submit} disabled={!form.title.trim()}>
              {editId ? "Guardar cambios" : "Registrar incidencia"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <PinDialog
        open={!!pinAction}
        onClose={() => setPinAction(null)}
        onConfirm={confirmPin}
        title={pinAction?.type === "delete" ? "Eliminar con PIN" : "Cambio protegido"}
        description="Esta acción saca la incidencia de vigilancia (o la elimina). Introduce tu PIN maestro para confirmar."
      />
    </div>
  );
}
